import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { platformIntegrationsService } from '../../services/api';

const { Text } = Typography;

const formatJson = (value) => {
  if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
    return '{}';
  }
  return JSON.stringify(value, null, 2);
};

const statusTag = (isActive) => (
  <Tag color={isActive ? 'green' : 'default'}>{isActive ? 'Ativo' : 'Inativo'}</Tag>
);

const PlatformIntegrationsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingProviderId, setUpdatingProviderId] = useState(null);
  const [providers, setProviders] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const loadProviders = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await platformIntegrationsService.listProviders({
        category: 'financial',
        active_only: false,
      });

      const rows = Array.isArray(response?.data) ? response.data : [];
      setProviders(rows);
    } catch (requestError) {
      console.error(requestError);
      setError('Nao foi possivel carregar o catalogo de providers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleToggleProviderStatus = async (provider) => {
    if (!provider?.id) {
      return;
    }

    const nextStatus = !Boolean(provider.is_active);
    setUpdatingProviderId(provider.id);

    try {
      await platformIntegrationsService.updateProviderStatus(provider.id, { is_active: nextStatus });
      message.success(`Provider ${nextStatus ? 'ativado' : 'desativado'} com sucesso.`);
      await loadProviders();
    } catch (requestError) {
      console.error(requestError);
      const detail = requestError?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Nao foi possivel atualizar o status do provider.');
    } finally {
      setUpdatingProviderId(null);
    }
  };

  const openDetails = (provider) => {
    setSelectedProvider(provider);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setSelectedProvider(null);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Provider',
        key: 'provider',
        render: (_, record) => (
          <div>
            <div>{record.display_name || '-'}</div>
            <Text type="secondary">{record.provider_key || '-'}</Text>
          </div>
        ),
      },
      {
        title: 'Categoria',
        dataIndex: 'category',
        key: 'category',
        render: (value) => value || '-',
      },
      {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (value) => statusTag(Boolean(value)),
      },
      {
        title: 'Tenants usando',
        dataIndex: 'tenants_using_count',
        key: 'tenants_using_count',
        render: (value) => Number(value || 0),
      },
      {
        title: 'Acoes',
        key: 'actions',
        render: (_, record) => {
          const isUpdating = updatingProviderId === record.id;
          const nextStatus = !Boolean(record.is_active);

          return (
            <Space>
              <Popconfirm
                title={`${nextStatus ? 'Ativar' : 'Desativar'} provider`}
                description={`Confirma ${nextStatus ? 'ativar' : 'desativar'} ${record.display_name || record.provider_key}?`}
                okText="Confirmar"
                cancelText="Cancelar"
                onConfirm={() => handleToggleProviderStatus(record)}
                disabled={isUpdating}
              >
                <Button size="small" loading={isUpdating}>
                  {nextStatus ? 'Ativar' : 'Desativar'}
                </Button>
              </Popconfirm>

              <Button size="small" onClick={() => openDetails(record)}>
                Ver detalhes
              </Button>
            </Space>
          );
        },
      },
    ],
    [updatingProviderId],
  );

  return (
    <Card title="Integracoes" style={{ marginTop: 24 }}>
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Desativar um provider remove apenas da selecao futura dos tenants. Configuracoes existentes e cobrancas ja criadas nao sao apagadas automaticamente."
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Erro ao carregar integracoes"
          description={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loading ? (
        <div style={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin tip="Carregando integracoes..." />
        </div>
      ) : (
        <Table
          rowKey={(record) => record.id}
          dataSource={providers}
          columns={columns}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'Nenhum provider encontrado' }}
        />
      )}

      <Drawer
        title="Detalhes do provider"
        open={detailsOpen}
        width={720}
        onClose={closeDetails}
        destroyOnClose
      >
        {selectedProvider ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Provider">{selectedProvider.display_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Provider Key">{selectedProvider.provider_key || '-'}</Descriptions.Item>
              <Descriptions.Item label="Categoria">{selectedProvider.category || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">{statusTag(Boolean(selectedProvider.is_active))}</Descriptions.Item>
              <Descriptions.Item label="Tenants usando">{Number(selectedProvider.tenants_using_count || 0)}</Descriptions.Item>
            </Descriptions>

            <Card size="small" title="credential_schema">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatJson(selectedProvider.credential_schema)}
              </pre>
            </Card>

            <Card size="small" title="config_schema">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatJson(selectedProvider.config_schema)}
              </pre>
            </Card>

            <Card size="small" title="capabilities">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatJson(selectedProvider.capabilities)}
              </pre>
            </Card>

            <Card size="small" title="runtime_defaults">
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatJson(selectedProvider.runtime_defaults)}
              </pre>
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </Card>
  );
};

export default PlatformIntegrationsPanel;
